import React, { useState, useMemo } from 'react';
import { Card, Button, Spinner, Badge, Modal } from '../ui';
import { AuditLogDetails } from '../ui/AuditLogDetails';
import { useQuery } from '@tanstack/react-query';
import { apiWorker, PaginatedResponse } from '../../api/worker';
import { useTenant } from '../../contexts/TenantContext';
import { safeDistanceToNow, safeFormat, cn } from '../../lib/utils';
import {
  Clock,
  Sliders,
  ShieldCheck,
  Building2,
  UserPlus,
  ShieldAlert,
  FileText,
  Mail,
  Activity,
  Eye,
  Search,
  RefreshCw,
  Download,
  Copy,
  Check,
  Filter,
  X,
  ChevronLeft,
  ChevronRight,
  Layers,
  User,
  Info
} from 'lucide-react';
import { toast } from 'react-hot-toast';

interface RecentActivityProps {
  isUniversal: boolean;
}

export const RecentActivity: React.FC<RecentActivityProps> = ({ isUniversal }) => {
  const { node } = useTenant();
  const [selectedLog, setSelectedLog] = useState<any | null>(null);
  const [isViewAllOpen, setIsViewAllOpen] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [fullPage, setFullPage] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [actionCategory, setActionCategory] = useState('ALL');

  // Query for Dashboard Widget (latest 6 records)
  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['recent-activity', isUniversal ? 'me' : node?.id],
    queryFn: async () => {
      const endpoint = isUniversal 
        ? '/api/activity/me' 
        : `/api/activity/nodes/${node?.id || 'global'}`;
      const response = await apiWorker.get<PaginatedResponse<any>>(endpoint, {
        params: { size: 6, sort: 'timestamp,desc' }
      });
      return response.data;
    },
    enabled: isUniversal || !!node?.id
  });

  // Query for Full History Modal
  const { data: fullData, isLoading: isFullLoading, refetch: refetchFull, isFetching: isFullFetching } = useQuery({
    queryKey: ['recent-activity-full', isUniversal ? 'me' : node?.id, fullPage],
    queryFn: async () => {
      const endpoint = isUniversal 
        ? '/api/activity/me' 
        : `/api/activity/nodes/${node?.id || 'global'}`;
      const response = await apiWorker.get<PaginatedResponse<any>>(endpoint, {
        params: { page: fullPage, size: 20, sort: 'timestamp,desc' }
      });
      return response.data;
    },
    enabled: isViewAllOpen && (isUniversal || !!node?.id)
  });

  const activities = data?.content || [];
  const fullActivities = fullData?.content || [];

  // Copy helper
  const handleCopy = (text: string, label: string = 'ID') => {
    navigator.clipboard.writeText(text);
    setCopiedId(text);
    toast.success(`Copied ${label} to clipboard`);
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Friendly action labels
  const getActionLabel = (action: string) => {
    const map: Record<string, string> = {
      MODULES_UPDATED: 'Modules Updated',
      MEMBER_ROLE_CHANGE: 'Role Changed',
      ROLE_UPDATED: 'Role Updated',
      ROLE_ASSIGNED: 'Role Assigned',
      IMPERSONATE_USER: 'User Impersonated',
      NODE_CREATED: 'Organization Created',
      NODE_UPDATED: 'Organization Updated',
      NODE_INITIALIZED: 'Organization Provisioned',
      MEMBER_JOINED: 'Member Joined',
      MEMBER_INVITED: 'Member Invited',
      MEMBER_REMOVED: 'Member Removed',
      SECURITY_BLOCK_IP: 'IP Address Blocked',
      DATABASE_INDEX_SYNC: 'Database Index Sync',
      SMS_CREDITS_ADDED: 'SMS Credits Added',
      VISIBILITY_UPDATED: 'Visibility Updated',
      SYSTEM_SETTINGS_UPDATE: 'System Settings Updated',
      CONFIG_UPDATED: 'Config Updated',
      BRANDING_UPDATED: 'Branding Updated',
      ELECTION_CREATED: 'Election Created',
      VOTE_CAST: 'Vote Cast',
      TRANSACTION_RECORDED: 'Transaction Recorded',
      VAULT_ASSET_ADDED: 'Vault File Added',
      VAULT_ASSET_DELETED: 'Vault File Deleted',
      REQUIREMENT_CREATED: 'Requirement Created',
      EVIDENCE_SUBMITTED: 'Evidence Submitted',
      EVIDENCE_VERIFIED: 'Evidence Verified',
      NEWSLETTER_PUBLISHED: 'Newsletter Published',
      TICKET_CREATED: 'Support Ticket Created',
      TICKET_STATUS_UPDATED: 'Ticket Status Updated'
    };
    return map[action] || action.replace(/_/g, ' ');
  };

  // Badge variant mapping
  const getActionBadgeVariant = (action: string): 'red' | 'purple' | 'amber' | 'blue' | 'green' | 'gray' => {
    if (action.includes('SECURITY') || action.includes('BLOCK') || action.includes('DELETE') || action.includes('REMOVE')) {
      return 'red';
    }
    if (action.includes('ROLE') || action.includes('IMPERSONATE') || action.includes('PERMISSION')) {
      return 'purple';
    }
    if (action.includes('MODULE') || action.includes('CONFIG') || action.includes('SETTING')) {
      return 'amber';
    }
    if (action.includes('NODE') || action.includes('SYNC') || action.includes('NEWSLETTER')) {
      return 'blue';
    }
    if (action.includes('MEMBER') || action.includes('INVITE') || action.includes('CREDIT') || action.includes('TRANSACTION')) {
      return 'green';
    }
    return 'gray';
  };

  // Category Icon Resolver
  const getActionIcon = (action: string) => {
    if (action.includes('MODULE') || action.includes('CONFIG') || action.includes('SETTING')) {
      return <Sliders size={18} className="text-amber-800 dark:text-amber-300" />;
    }
    if (action.includes('ROLE') || action.includes('PERMISSION')) {
      return <ShieldCheck size={18} className="text-purple-800 dark:text-purple-300" />;
    }
    if (action.includes('NODE') || action.includes('INITIAL')) {
      return <Building2 size={18} className="text-blue-800 dark:text-blue-300" />;
    }
    if (action.includes('MEMBER') || action.includes('USER') || action.includes('INVITE')) {
      return <UserPlus size={18} className="text-emerald-800 dark:text-emerald-300" />;
    }
    if (action.includes('SECURITY') || action.includes('BLOCK') || action.includes('ALERT')) {
      return <ShieldAlert size={18} className="text-red-800 dark:text-red-300" />;
    }
    if (action.includes('VAULT') || action.includes('FILE') || action.includes('DOC')) {
      return <FileText size={18} className="text-indigo-800 dark:text-indigo-300" />;
    }
    if (action.includes('NEWSLETTER') || action.includes('MAIL')) {
      return <Mail size={18} className="text-cyan-800 dark:text-cyan-300" />;
    }
    return <Activity size={18} className="text-black dark:text-white" />;
  };

  // Human resolver for Actor
  const getActorInfo = (log: any) => {
    if (log.actorName) {
      const isSys = String(log.actorName).toLowerCase().includes('system');
      return {
        name: isSys ? 'System Daemon' : log.actorName,
        email: log.actorEmail,
        id: log.userId || '',
        isSystem: isSys
      };
    }
    const rawId = log.userId || log.actor || log.user || '';
    if (!rawId || rawId === 'SYSTEM' || rawId === 'system_daemon' || rawId === 'system') {
      return { name: 'System Daemon', email: undefined, id: 'system', isSystem: true };
    }
    const shortId = rawId.length > 18 ? `${rawId.slice(0, 10)}...${rawId.slice(-4)}` : rawId;
    return {
      name: shortId,
      email: undefined,
      id: rawId,
      isSystem: false
    };
  };

  // Human resolver for Target
  const getTargetInfo = (log: any) => {
    if (log.targetName) {
      return {
        name: log.targetName,
        slug: log.nodeSlug,
        id: log.targetId || log.nodeId || ''
      };
    }
    if (log.nodeName) {
      return {
        name: log.nodeName,
        slug: log.nodeSlug,
        id: log.nodeId || ''
      };
    }
    const targetId = log.targetId || log.nodeId || '';
    if (!targetId || targetId === 'platform-root' || targetId === 'global') {
      return { name: node?.name || 'Platform Scope', slug: node?.slug || 'global', id: targetId };
    }
    return {
      name: targetId.length > 18 ? `${targetId.slice(0, 8)}...${targetId.slice(-4)}` : targetId,
      id: targetId
    };
  };

  // Filter full activities in "View All" modal
  const filteredFullActivities = useMemo(() => {
    return fullActivities.filter((item: any) => {
      const action = item.action || '';
      const actionLabel = getActionLabel(action);
      const actor = getActorInfo(item);
      const target = getTargetInfo(item);

      if (actionCategory !== 'ALL') {
        if (actionCategory === 'MODULES' && !action.includes('MODULE')) return false;
        if (actionCategory === 'ROLES' && !action.includes('ROLE')) return false;
        if (actionCategory === 'MEMBERS' && !action.includes('MEMBER') && !action.includes('INVITE')) return false;
        if (actionCategory === 'NODES' && !action.includes('NODE')) return false;
      }

      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchesAction = action.toLowerCase().includes(q) || actionLabel.toLowerCase().includes(q);
        const matchesActor = actor.name.toLowerCase().includes(q) || (actor.email && actor.email.toLowerCase().includes(q)) || actor.id.toLowerCase().includes(q);
        const matchesTarget = target.name.toLowerCase().includes(q) || target.id.toLowerCase().includes(q);
        const matchesMeta = item.metadata ? JSON.stringify(item.metadata).toLowerCase().includes(q) : false;

        if (!matchesAction && !matchesActor && !matchesTarget && !matchesMeta) {
          return false;
        }
      }

      return true;
    });
  }, [fullActivities, searchQuery, actionCategory]);

  // CSV Exporter
  const handleExportCsv = () => {
    if (filteredFullActivities.length === 0) {
      toast.error('No activities to export.');
      return;
    }
    const headers = ['Timestamp', 'Actor Name', 'Actor Email', 'Actor ID', 'Action', 'Target Name', 'Target ID', 'Status', 'Metadata'];
    const rows = filteredFullActivities.map((l: any) => {
      const actor = getActorInfo(l);
      const target = getTargetInfo(l);
      return [
        `"${l.timestamp || l.createdAt || ''}"`,
        `"${actor.name.replace(/"/g, '""')}"`,
        `"${(actor.email || '').replace(/"/g, '""')}"`,
        `"${actor.id}"`,
        `"${getActionLabel(l.action || '')}"`,
        `"${target.name.replace(/"/g, '""')}"`,
        `"${target.id}"`,
        `"${l.status || 'SUCCESS'}"`,
        `"${l.metadata ? JSON.stringify(l.metadata).replace(/"/g, '""') : ''}"`
      ];
    });
    const csvContent = [headers.join(','), ...rows.map((r: any) => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `system-log-${node?.slug || 'activity'}-${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filteredFullActivities.length} activity records to CSV`);
  };

  return (
    <div className="bg-white border-8 border-black dark:border-white p-6 lg:p-8 shadow-[8px_8px_0px_#000000] dark:bg-black dark:shadow-[8px_8px_0px_#FFFFFF] lg:col-span-2 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between border-b-4 border-black dark:border-white pb-4 mb-6">
        <div className="flex items-center gap-3">
          <h3 className="font-black font-display text-2xl uppercase tracking-tight text-black dark:text-white">
            System Log
          </h3>
          <span className="hidden sm:inline-flex items-center gap-1.5 px-2 py-0.5 text-[10px] font-mono font-bold bg-[#FFD600] text-black border-2 border-black dark:border-white shadow-[2px_2px_0px_#000000] dark:shadow-[2px_2px_0px_#FFFFFF]">
            <Activity size={10} className="animate-pulse" /> AUDIT FEED
          </span>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            title="Refresh feed"
            className="p-1.5 border-2 border-black dark:border-white bg-white dark:bg-black hover:bg-[#FFD600] dark:hover:bg-[#FFD600] dark:hover:text-black transition-colors shadow-[2px_2px_0px_#000000] dark:shadow-[2px_2px_0px_#FFFFFF]"
          >
            <RefreshCw size={14} className={cn(isFetching && "animate-spin text-black")} />
          </button>
          
          <Button
            variant="ghost"
            size="sm"
            className="border-2 border-black dark:border-white rounded-none uppercase font-mono tracking-widest text-[10px] font-bold hover:bg-black hover:text-white dark:hover:bg-white dark:hover:text-black shadow-[2px_2px_0px_#000000] dark:shadow-[2px_2px_0px_#FFFFFF]"
            onClick={() => setIsViewAllOpen(true)}
          >
            View All
          </Button>
        </div>
      </div>
      
      {/* Content */}
      {isLoading ? (
        <div className="py-12 flex justify-center border-2 border-dashed border-black dark:border-white flex-1 items-center">
          <Spinner size="sm" />
        </div>
      ) : activities.length > 0 ? (
        <div className="space-y-3 flex-1">
          {activities.map((activity: any, index: number) => {
            const actor = getActorInfo(activity);
            const target = getTargetInfo(activity);
            const action = activity.action || activity.event || activity.type || 'SYSTEM_ACTION';
            const actionLabel = getActionLabel(action);
            const createdAt = activity.createdAt || activity.timestamp || new Date();
            
            return (
              <div
                key={activity.id || `activity-${index}`}
                onClick={() => setSelectedLog(activity)}
                className="flex items-start gap-4 p-4 border-2 border-black dark:border-white bg-white dark:bg-black hover:bg-[#FFD600]/10 dark:hover:bg-[#FFD600]/10 transition-all cursor-pointer group shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] hover:translate-x-0.5 hover:-translate-y-0.5"
              >
                {/* Category Icon */}
                <div className="w-10 h-10 border-2 border-black dark:border-white bg-[#FFD600] text-black flex-shrink-0 flex items-center justify-center font-bold shadow-[2px_2px_0px_#000000] dark:shadow-[2px_2px_0px_#FFFFFF]">
                  {getActionIcon(action)}
                </div>

                {/* Main Text */}
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <Badge variant={getActionBadgeVariant(action)} className="font-mono text-[10px] tracking-wider uppercase font-bold">
                      {actionLabel}
                    </Badge>
                    
                    <span className="text-xs font-mono font-bold text-gray-800 dark:text-gray-200">
                      {actor.isSystem ? (
                        <span className="text-gray-500 dark:text-gray-400">System Automated</span>
                      ) : (
                        <span>by <strong className="text-black dark:text-white font-black">{actor.name}</strong></span>
                      )}
                    </span>

                    {target.name && (
                      <span className="text-[11px] font-mono text-gray-500 dark:text-gray-400 flex items-center gap-1">
                        • <span>{target.name}</span>
                      </span>
                    )}

                    <span className="ml-auto text-[11px] font-bold text-[#0044FF] dark:text-[#FFD600] font-mono flex items-center gap-1">
                      <Clock size={11} /> {safeDistanceToNow(createdAt)} ago
                    </span>
                  </div>

                  {/* Formatted Changes Summary */}
                  <div className="mt-1">
                    <AuditLogDetails action={action} metadata={activity.metadata} />
                  </div>
                </div>

                {/* Inspect Button Pill */}
                <div className="hidden sm:flex opacity-0 group-hover:opacity-100 transition-opacity self-center flex-shrink-0">
                  <span className="px-2 py-1 border border-black dark:border-white bg-white dark:bg-black text-black dark:text-white flex items-center gap-1 text-[10px] font-mono font-bold uppercase shadow-[2px_2px_0px_#000000] dark:shadow-[2px_2px_0px_#FFFFFF]">
                    <Eye size={12} /> Inspect
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="py-12 flex-1 flex flex-col items-center justify-center text-xs font-bold text-gray-500 font-mono uppercase tracking-widest border-2 border-dashed border-black dark:border-white">
          <Activity size={32} className="mb-2 text-gray-400 opacity-60" />
          No recent system activity recorded
        </div>
      )}

      {/* ======================= VIEW ALL FULL HISTORY MODAL ======================= */}
      <Modal
        isOpen={isViewAllOpen}
        onClose={() => setIsViewAllOpen(false)}
        title={`System Log & Audit Trail — ${node?.name || 'Platform'}`}
        className="md:max-w-4xl border-4 border-black dark:border-white shadow-[12px_12px_0px_#000000] dark:shadow-[12px_12px_0px_#FFFFFF] rounded-none"
      >
        <div className="space-y-4">
          {/* Controls Bar */}
          <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center justify-between pb-3 border-b-2 border-black dark:border-white">
            <div className="relative flex-1">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              <input
                type="text"
                placeholder="Filter activities by actor, action, or metadata..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-1.5 text-xs font-mono border-2 border-black dark:border-white rounded-none bg-white dark:bg-black text-black dark:text-white focus:outline-none focus:bg-[#FFD600]/10"
              />
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center gap-1">
                <Filter size={12} className="text-gray-500" />
                <select
                  value={actionCategory}
                  onChange={(e) => setActionCategory(e.target.value)}
                  className="px-2 py-1.5 text-xs font-mono font-bold uppercase border-2 border-black dark:border-white bg-white dark:bg-black text-black dark:text-white rounded-none focus:outline-none"
                >
                  <option value="ALL">All Actions</option>
                  <option value="MODULES">Modules</option>
                  <option value="ROLES">Roles</option>
                  <option value="MEMBERS">Members</option>
                  <option value="NODES">Organizations</option>
                </select>
              </div>

              <button
                onClick={() => refetchFull()}
                disabled={isFullFetching}
                title="Refresh full history"
                className="p-1.5 border-2 border-black dark:border-white bg-white dark:bg-black hover:bg-[#FFD600] dark:hover:bg-[#FFD600] dark:hover:text-black transition-colors"
              >
                <RefreshCw size={14} className={cn(isFullFetching && "animate-spin")} />
              </button>

              <button
                onClick={handleExportCsv}
                className="px-3 py-1.5 border-2 border-black dark:border-white bg-white dark:bg-black hover:bg-[#FFD600] dark:hover:bg-[#FFD600] dark:hover:text-black text-[11px] font-mono font-bold uppercase flex items-center gap-1.5 shadow-[2px_2px_0px_#000000] dark:shadow-[2px_2px_0px_#FFFFFF]"
              >
                <Download size={12} /> Export CSV
              </button>
            </div>
          </div>

          {/* List Table */}
          {isFullLoading ? (
            <div className="py-16 flex justify-center items-center">
              <Spinner size="md" />
            </div>
          ) : filteredFullActivities.length > 0 ? (
            <div className="space-y-2 max-h-[55vh] overflow-y-auto pr-1">
              {filteredFullActivities.map((act: any, idx: number) => {
                const actor = getActorInfo(act);
                const target = getTargetInfo(act);
                const action = act.action || act.event || act.type || 'SYSTEM_ACTION';
                const actionLabel = getActionLabel(action);
                const createdAt = act.createdAt || act.timestamp || new Date();

                return (
                  <div
                    key={act.id || `full-${idx}`}
                    onClick={() => setSelectedLog(act)}
                    className="flex items-center gap-3 p-3 border-2 border-black dark:border-white bg-white dark:bg-black hover:bg-[#FFD600]/10 transition-colors cursor-pointer group"
                  >
                    <div className="w-8 h-8 border border-black dark:border-white bg-[#FFD600] text-black flex-shrink-0 flex items-center justify-center font-bold">
                      {getActionIcon(action)}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant={getActionBadgeVariant(action)} className="font-mono text-[9px] font-bold">
                          {actionLabel}
                        </Badge>
                        <span className="text-xs font-mono font-bold text-gray-800 dark:text-gray-200">
                          {actor.isSystem ? 'System Daemon' : actor.name}
                        </span>
                        {target.name && (
                          <span className="text-[10px] font-mono text-gray-500">
                            • {target.name}
                          </span>
                        )}
                        <span className="ml-auto text-[10px] font-mono text-gray-500 flex items-center gap-1">
                          <Clock size={10} /> {safeFormat(createdAt, 'MMM d, yyyy HH:mm:ss')}
                        </span>
                      </div>
                      <div className="mt-1">
                        <AuditLogDetails action={action} metadata={act.metadata} />
                      </div>
                    </div>

                    <Button variant="ghost" size="sm" className="opacity-0 group-hover:opacity-100 p-1 border border-black dark:border-white text-[10px] uppercase font-mono">
                      <Eye size={12} />
                    </Button>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="py-12 text-center text-xs font-mono text-gray-500">
              No activity logs match the current search or filters.
            </div>
          )}

          {/* Pagination Controls */}
          {fullData && fullData.totalPages > 1 && (
            <div className="flex items-center justify-between pt-3 border-t-2 border-black dark:border-white text-xs font-mono">
              <span className="text-gray-500 font-bold">
                Page {fullData.number + 1} of {fullData.totalPages} ({fullData.totalElements} events)
              </span>
              <div className="flex items-center gap-1">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={fullData.first}
                  onClick={() => setFullPage((p) => Math.max(0, p - 1))}
                  className="rounded-none border-2 border-black dark:border-white px-2 py-1 text-xs"
                >
                  <ChevronLeft size={14} /> Prev
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={fullData.last}
                  onClick={() => setFullPage((p) => p + 1)}
                  className="rounded-none border-2 border-black dark:border-white px-2 py-1 text-xs"
                >
                  Next <ChevronRight size={14} />
                </Button>
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* ======================= INSPECT RECORD MODAL ======================= */}
      {selectedLog && (
        <Modal
          isOpen={!!selectedLog}
          onClose={() => setSelectedLog(null)}
          title="Audit Record Inspector"
          className="md:max-w-2xl border-4 border-black dark:border-white shadow-[12px_12px_0px_#000000] dark:shadow-[12px_12px_0px_#FFFFFF] rounded-none"
        >
          <div className="space-y-4">
            {/* Header Strip */}
            <div className="flex items-center justify-between p-3 border-2 border-black dark:border-white bg-[#FFD600] text-black">
              <div className="flex items-center gap-2">
                <Badge variant={getActionBadgeVariant(selectedLog.action || '')} className="font-mono text-xs font-bold uppercase border-black">
                  {getActionLabel(selectedLog.action || '')}
                </Badge>
                <span className="font-mono text-xs font-black">
                  STATUS: {selectedLog.status || 'SUCCESS'}
                </span>
              </div>
              <span className="text-xs font-mono font-bold flex items-center gap-1">
                <Clock size={12} /> {safeFormat(selectedLog.timestamp || selectedLog.createdAt || new Date(), 'MMM d, yyyy HH:mm:ss')}
              </span>
            </div>

            {/* Information Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* Actor Card */}
              <div className="p-3 border-2 border-black dark:border-white bg-white dark:bg-black">
                <div className="flex items-center gap-1.5 text-xs font-bold font-mono text-gray-500 uppercase mb-2">
                  <User size={13} /> Actor Details
                </div>
                {(() => {
                  const act = getActorInfo(selectedLog);
                  return (
                    <div className="space-y-1">
                      <p className="text-sm font-black font-display text-black dark:text-white uppercase">{act.name}</p>
                      {act.email && <p className="text-xs font-mono text-gray-600 dark:text-gray-400">{act.email}</p>}
                      {act.id && (
                        <div className="flex items-center gap-1 mt-2">
                          <code className="text-[10px] font-mono bg-gray-100 dark:bg-slate-800 px-1.5 py-0.5 border border-black dark:border-white truncate flex-1">
                            {act.id}
                          </code>
                          <button
                            onClick={() => handleCopy(act.id, 'Actor ID')}
                            className="p-1 border border-black dark:border-white hover:bg-[#FFD600] dark:hover:bg-[#FFD600] dark:hover:text-black transition-colors"
                            title="Copy Actor ID"
                          >
                            {copiedId === act.id ? <Check size={12} className="text-green-600" /> : <Copy size={12} />}
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>

              {/* Target Scope Card */}
              <div className="p-3 border-2 border-black dark:border-white bg-white dark:bg-black">
                <div className="flex items-center gap-1.5 text-xs font-bold font-mono text-gray-500 uppercase mb-2">
                  <Building2 size={13} /> Target / Scope
                </div>
                {(() => {
                  const tgt = getTargetInfo(selectedLog);
                  return (
                    <div className="space-y-1">
                      <p className="text-sm font-black font-display text-black dark:text-white uppercase">{tgt.name}</p>
                      {tgt.slug && <p className="text-xs font-mono text-[#0044FF] dark:text-[#FFD600]">/{tgt.slug}</p>}
                      {tgt.id && (
                        <div className="flex items-center gap-1 mt-2">
                          <code className="text-[10px] font-mono bg-gray-100 dark:bg-slate-800 px-1.5 py-0.5 border border-black dark:border-white truncate flex-1">
                            {tgt.id}
                          </code>
                          <button
                            onClick={() => handleCopy(tgt.id, 'Target ID')}
                            className="p-1 border border-black dark:border-white hover:bg-[#FFD600] dark:hover:bg-[#FFD600] dark:hover:text-black transition-colors"
                            title="Copy Target ID"
                          >
                            {copiedId === tgt.id ? <Check size={12} className="text-green-600" /> : <Copy size={12} />}
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Changes Summary View */}
            <div className="p-3 border-2 border-black dark:border-white bg-white dark:bg-black">
              <div className="flex items-center gap-1.5 text-xs font-bold font-mono text-gray-500 uppercase mb-2">
                <Layers size={13} /> Change Summary
              </div>
              <AuditLogDetails action={selectedLog.action || ''} metadata={selectedLog.metadata} />
            </div>

            {/* Raw JSON Payload */}
            <div className="p-3 border-2 border-black dark:border-white bg-gray-50 dark:bg-slate-950">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-mono font-bold uppercase text-gray-500">Structured Payload</span>
                <button
                  onClick={() => handleCopy(JSON.stringify(selectedLog, null, 2), 'JSON Payload')}
                  className="px-2 py-1 text-[10px] font-mono font-bold uppercase border border-black dark:border-white bg-white dark:bg-black hover:bg-[#FFD600] dark:hover:bg-[#FFD600] dark:hover:text-black transition-colors flex items-center gap-1"
                >
                  <Copy size={10} /> Copy JSON
                </button>
              </div>
              <pre className="text-[11px] font-mono bg-black text-[#00FF66] p-3 overflow-x-auto max-h-48 border border-black dark:border-white">
                {JSON.stringify(selectedLog, null, 2)}
              </pre>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};
